// apps/web/src/pages/vendas/importacao/passo-mapeamento.tsx — Fase 12, Plano 25 (PV-03, D-31).
// Passo 2: "Campo do PV ↔ Coluna da planilha", com amostra da 1ª linha e mapeamento salvo por cliente.
import { Info, Loader2 } from 'lucide-react';
import type { CampoImportacaoPv, LeituraPlanilha } from '@pieltec/contracts';
import { Button } from '../../../components/ui/button';
import { Checkbox } from '../../../components/ui/checkbox';
import {
  AVISO_SEM_NUMERO_PEDIDO,
  CAMPOS_ROTULOS,
  mapeamentoObrigatorioCompleto,
  rotuloColuna,
  type ColunasEscolhidas,
} from '../../../lib/vendas/importacao';
import { CampoSelect } from '../../../components/ui/campo-select';

interface PassoMapeamentoProps {
  readonly clienteNome: string;
  readonly leitura: LeituraPlanilha;
  readonly colunas: ColunasEscolhidas;
  readonly onColuna: (campo: CampoImportacaoPv, valor: string) => void;
  /** true quando o mapeamento inicial veio salvo do cliente (e ainda não foi trocado). */
  readonly mapeamentoAplicado: boolean;
  readonly salvar: boolean;
  readonly onSalvar: (valor: boolean) => void;
  readonly erro: string | null;
  readonly ocupado: boolean;
  readonly onVoltar: () => void;
  readonly onGerar: () => void;
}

export function PassoMapeamento(props: PassoMapeamentoProps) {
  const { leitura, colunas, erro, ocupado } = props;
  const semNumeroPedido = (colunas.numeroPedido ?? '') === '';
  const completo = mapeamentoObrigatorioCompleto(colunas);

  return (
    <div className="flex flex-col gap-4">
      {props.mapeamentoAplicado && (
        <p role="status" className="flex items-center gap-2 rounded-md border border-slate-200 bg-slate-50 px-3 py-2 text-sm">
          <Info className="size-4 shrink-0" aria-hidden="true" />
          {`Mapeamento de ${props.clienteNome} aplicado. Ajuste se precisar.`}
        </p>
      )}

      <table className="w-full max-w-3xl text-sm">
        <caption className="sr-only">Campo do PV e coluna da planilha</caption>
        <thead className="bg-slate-100 text-left">
          <tr>
            <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Campo do PV</th>
            <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Coluna da planilha</th>
            <th scope="col" className="px-3 py-2 text-xs font-semibold uppercase tracking-wider">Amostra (1ª linha)</th>
          </tr>
        </thead>
        <tbody>
          {CAMPOS_ROTULOS.map(({ campo, rotulo }) => {
            const escolhida = colunas[campo] ?? '';
            const amostra = escolhida === '' ? '—' : (leitura.amostra[Number(escolhida)] ?? '—');
            const idCampo = `mapa-${campo}`;
            return (
              <tr key={campo} className="border-t">
                <td className="px-3 py-2">
                  <label htmlFor={idCampo}>{rotulo}</label>
                </td>
                <td className="px-3 py-2">
                  <CampoSelect
                    id={idCampo}
                    value={escolhida}
                    disabled={ocupado}
                    onValueChange={(valor) => props.onColuna(campo, valor)}
                    options={[{ value: '', label: 'Selecionar coluna' }, ...leitura.cabecalhos.map((cabecalho, indice) => ({ value: String(indice), label: rotuloColuna(cabecalho, indice) }))]}
                    className="min-w-[12rem]"
                  />
                </td>
                <td className="max-w-[16rem] truncate px-3 py-2 text-sm text-muted-foreground">{amostra}</td>
              </tr>
            );
          })}
        </tbody>
      </table>

      {semNumeroPedido && (
        <p role="note" className="text-sm text-amber-700">
          {AVISO_SEM_NUMERO_PEDIDO}
        </p>
      )}

      <label htmlFor="mapa-salvar" className="flex min-h-[var(--control-height,2rem)] cursor-pointer items-center gap-2 text-sm">
        <Checkbox id="mapa-salvar" checked={props.salvar} disabled={ocupado} onCheckedChange={(marcada) => props.onSalvar(marcada === true)} />
        {`Salvar este mapeamento para ${props.clienteNome}`}
      </label>

      {erro && (
        <p role="alert" className="text-sm text-rose-700">
          {erro}
        </p>
      )}

      <div className="sticky bottom-0 flex items-center gap-2 border-t bg-background py-3">
        <Button type="button" variant="outline" disabled={ocupado} onClick={props.onVoltar}>
          Voltar
        </Button>
        <Button type="button" disabled={!completo || ocupado} onClick={props.onGerar}>
          {ocupado && <Loader2 className="animate-spin" aria-hidden="true" />}
          Gerar prévia
        </Button>
      </div>
    </div>
  );
}
